import type { ReactNode } from 'react';

import { PeriodResultKind } from '@/entities/result';
import type { PeriodResult } from '@/entities/result';
import { formatMoscowMonth, formatScore } from '@/shared/lib';

import { ResultCounter, ResultPrizeName } from '../ui/ResultModal';
import { RESULT_VARIANTS } from './variants';
import { variantOf } from './variant-of';

/**
 * Итог, у которого есть место.
 *
 * Всё, кроме Финала сезона: там жеребьёвка среди допущенных, места нет
 * вовсе (ГДД §11.3) и `rank` приходит `null`. Финал рисует своя модалка
 * (`widgets/season-final-modal`), а предикат делает это гарантией
 * компилятора, а не договорённостью: заголовки каталога содержат «999»
 * из макета, и молчаливый откат на них показал бы игроку «Ты на 999 месте».
 */
export function hasRank(
  result: PeriodResult,
): result is PeriodResult & { rank: number } {
  return result.rank !== null;
}

export interface ResultContent {
  shape: (typeof RESULT_VARIANTS)[keyof typeof RESULT_VARIANTS]['shape'];
  image: (typeof RESULT_VARIANTS)[keyof typeof RESULT_VARIANTS]['image'];
  title: string;
  body: ReactNode;
  score?: ReactNode;
  caption?: ReactNode;
}

/**
 * Текст и счётчики модалки из фактов сервера.
 *
 * Форма (`shape`, `image`) и подписи-константы берутся из свёрстанного
 * каталога `RESULT_VARIANTS` — там же, где их задал дизайнер. Числа,
 * место и месяц подставляются из ответа: в каталоге они «999» и
 * «сентябрь» из макета.
 */
export function resultContent(
  result: PeriodResult & { rank: number },
): ResultContent {
  const variant = RESULT_VARIANTS[variantOf(result)];
  const isWeekly = result.kind === PeriodResultKind.WEEKLY_TOURNAMENT;
  const month = formatMoscowMonth(result.periodStartsAt);
  // «в розыгрыше ЗА август», а не «за августа»: предлог требует
  // винительного, а он у месяцев совпадает с именительным
  const monthAfterZa = formatMoscowMonth(result.periodStartsAt, 'nominative');
  const nextMonth = result.nextPeriodStartsAt
    ? formatMoscowMonth(result.nextPeriodStartsAt, 'prepositional')
    : null;

  return {
    shape: variant.shape,
    image: variant.image,
    title: `Ты на ${formatScore(result.rank)} месте`,
    body:
      bodyOf(result, isWeekly, month, monthAfterZa, nextMonth) ?? variant.body,
    score: scoreOf(result),
    caption: variant.caption,
  };
}

function bodyOf(
  result: PeriodResult,
  isWeekly: boolean,
  month: string,
  monthAfterZa: string,
  nextMonth: string | null,
): ReactNode {
  if (isWeekly) return null;

  if (result.isWinner) {
    return result.prizeTitle && result.prizeKind !== 'FREEBETS' ? (
      `И выиграл главный приз ${month}`
    ) : (
      <>
        Поздравляем с победой
        <br />в розыгрыше за {monthAfterZa}
      </>
    );
  }

  if (result.rank === 1) {
    return (
      <>
        Красавчик! Ты лучший игрок
        <br />
        по итогам {month}.
        {nextMonth ? ` Сможешь удержать позицию в ${nextMonth}?` : null}
      </>
    );
  }

  // «Заберёшься ещё выше в октябре?» — вопрос уместен, только если
  // следующий период существует
  return nextMonth
    ? `Это твоё место по итогам ${month}. Заберёшься ещё выше в ${nextMonth}?`
    : `Это твоё место по итогам ${month}`;
}

function scoreOf(result: PeriodResult): ReactNode {
  const parts: ReactNode[] = [];

  if (result.prizeTitle && result.prizeKind !== 'FREEBETS') {
    parts.push(
      <ResultPrizeName key="prize">{result.prizeTitle}</ResultPrizeName>,
    );
  }
  if (result.pointsAwarded > 0) {
    parts.push(
      <ResultCounter
        key="points"
        icon="points"
        value={formatScore(result.pointsAwarded)}
      />,
    );
  }
  if (result.freebetsAwarded > 0) {
    parts.push(
      <ResultCounter
        key="freebets"
        icon="freebets"
        value={formatScore(result.freebetsAwarded)}
      />,
    );
  }

  return parts.length > 0 ? <>{parts}</> : undefined;
}
